// src/realtime/socket-client.js

import { io } from "socket.io-client";
import {appConfig} from "../app-config";

class SocketClient {
    constructor() {
        this.socket = null;
        this.currentToken = null;
        this.currentWorkspaceId = null;
        this.currentWorkspaceDocumentId = null;
    }

    getSocketUrl() {
        return appConfig.socketUrl;
    }

    connect({ token, workspaceId, workspaceDocumentId }) {
        if (!token) {
            console.warn("[SocketClient] token is required");
            return null;
        }

        if (!workspaceId) {
            console.warn("[SocketClient] workspaceId is required");
            return null;
        }

        const socketUrl = this.getSocketUrl();

        const tokenChanged = this.currentToken !== token;
        const workspaceChanged = this.currentWorkspaceId !== workspaceId;
        const workspaceDocumentChanged = this.currentWorkspaceDocumentId !== workspaceDocumentId;

        // Reuse the same socket while it is connecting as well. React effects can
        // re-run before the first handshake completes; creating another socket in
        // that window leaves multiple transports reconnecting in the background.
        if (this.socket && !tokenChanged && !workspaceChanged && !workspaceDocumentChanged) {
            return this.socket;
        }

        if (this.socket && (tokenChanged || workspaceChanged || workspaceDocumentChanged)) {
            console.log("[SocketClient] reconnecting because auth changed", {
                tokenChanged,
                workspaceChanged,
                workspaceDocumentChanged,
            });

            this.disconnect();
        }

        this.currentToken = token;
        this.currentWorkspaceId = workspaceId;
        this.currentWorkspaceDocumentId = workspaceDocumentId;

        this.socket = io(socketUrl, {
            auth: {
                token,
                workspaceId,
                workspaceDocumentId,
            },
            // Default stays websocket-only; hosts whose proxy cannot forward WebSocket upgrades
            // build with REACT_APP_SOCKET_TRANSPORTS=polling,websocket to fall back to HTTP polling.
            transports: (process.env.REACT_APP_SOCKET_TRANSPORTS || "websocket")
                .split(",")
                .map((transport) => transport.trim())
                .filter(Boolean),
            reconnection: true,
            reconnectionAttempts: 10,
            reconnectionDelay: 1000,
        });

        this.socket.on("connect", () => {
            console.log("[SocketClient] connected", {
                socketId: this.socket.id,
                workspaceId,
            });
        });

        this.socket.on("disconnect", (reason) => {
            console.log("[SocketClient] disconnected", {
                reason,
                workspaceId: this.currentWorkspaceId,
            });
        });

        this.socket.on("connect_error", (error) => {
            console.error("[SocketClient] connection error:", {
                message: error.message,
                code: error.data?.code || null,
            });
        });

        return this.socket;
    }

    getSocket() {
        return this.socket;
    }

    isConnected() {
        return Boolean(this.socket && this.socket.connected);
    }

    disconnect() {
        if (this.socket) {
            this.socket.removeAllListeners();
            this.socket.disconnect();
            this.socket = null;
        }

        this.currentToken = null;
        this.currentWorkspaceId = null;
        this.currentWorkspaceDocumentId = null;
    }

    emit(event, payload) {
        if (!this.socket) {
            console.warn("[SocketClient] Socket is not initialized");
            return;
        }

        if (!this.socket.connected) {
            console.warn("[SocketClient] Socket is not connected");
            return;
        }

        this.socket.emit(event, payload);
    }

    on(event, callback) {
        if (!this.socket) {
            console.warn("[SocketClient] Socket is not initialized");
            return;
        }

        this.socket.on(event, callback);
    }

    off(event, callback) {
        if (!this.socket) {
            return;
        }

        if (callback) {
            this.socket.off(event, callback);
        } else {
            this.socket.off(event);
        }
    }
}

export const socketClient = new SocketClient();
